import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 enabled:cursor-pointer whitespace-nowrap transition-all text-sm font-medium focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground hover:bg-primary/90 [&_svg[data-loading]]:text-primary-foreground!",
				info: "bg-info text-info-foreground hover:bg-info/90 [&_svg[data-loading]]:text-info-foreground!",
				infoMuted:
					"bg-info-muted text-info-foreground hover:bg-info-muted/90 [&_svg[data-loading]]:text-info-foreground!",
				warning:
					"bg-warning text-warning-foreground hover:bg-warning/90 [&_svg[data-loading]]:text-warning-foreground!",
				warningMuted:
					"bg-warning-muted text-warning-foreground hover:bg-warning-muted/90 [&_svg[data-loading]]:text-warning-foreground!",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90 [&_svg[data-loading]]:text-destructive-foreground!",
				destructiveMuted:
					"bg-destructive-muted text-destructive-muted-foreground hover:bg-destructive-muted/90 [&_svg[data-loading]]:text-destructive-muted-foreground!",
				success:
					"bg-success text-success-foreground hover:bg-success/90 [&_svg[data-loading]]:text-success-foreground!",
				successMuted:
					"bg-success-muted text-success-foreground hover:bg-success-muted/90 [&_svg[data-loading]]:text-success-foreground!",
				outline:
					"border border-input  hover:bg-accent hover:text-accent-foreground [&_svg[data-loading]]:text-accent-foreground!",
				secondary:
					"bg-secondary text-secondary-foreground hover:bg-secondary/80 [&_svg[data-loading]]:text-secondary-foreground!",
				tertiary:
					"bg-tertiary text-tertiary-foreground hover:bg-tertiary/80 [&_svg[data-loading]]:text-tertiary-foreground!",
				triffinCredit:
					"bg-[linear-gradient(135deg,hsl(var(--teal-650)),hsl(var(--teal-300)))] text-[hsl(var(--green-500))] hover:bg-[linear-gradient(135deg,hsl(var(--green-500)),hsl(var(--green-400)))] hover:text-[hsl(var(--teal-500))] [&_svg[data-loading]]:text-[hsl(var(--green-500))]!",
				ghost: "hover:bg-accent hover:text-accent-foreground [&_svg[data-loading]]:text-accent-foreground!",
				link: "text-foreground underline-offset-4 underline [&_svg[data-loading]]:text-foreground!",
				neutral:
					"bg-neutral text-neutral-foreground hover:bg-neutral/80 [&_svg[data-loading]]:text-neutral-foreground!",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 px-3 text-xs",
				lg: "h-11 px-8",
				icon: "size-10",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	isLoading?: boolean;
	componentOverride?: React.ElementType;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, isLoading, disabled, children, componentOverride, ...props }, ref) => {
		const Comp = componentOverride ?? (asChild ? Slot : "button");

		return (
			<Comp
				ref={ref}
				disabled={isLoading || disabled}
				className={cn(buttonVariants({ variant, size, className }), {
					"[&]:text-transparent [&>*]:invisible relative": isLoading,
				})}
				{...props}
			>
				{asChild ? (
					children
				) : (
					<>
						{children}
						{isLoading && <Loader2 data-loading className="visible! absolute h-5 w-5 animate-spin" />}
					</>
				)}
			</Comp>
		);
	},
);
Button.displayName = "Button";

export { Button, type ButtonProps, buttonVariants };
